<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <div class="dbg-root" tabindex="0" @keydown="onKey">
            <!-- toolbar -->
            <!-- IE 2026 09 23 / ODOO-341407: i18n -->
            <div class="dbg-toolbar">
                <button class="dbg-btn dbg-test" @click="startDebug" :disabled="running"
                        v-wjTooltip="t('Run in debug mode')">
                    <i class="pi pi-caret-right"></i> {{ t('TEST') }}
                </button>
                <span class="dbg-sep"></span>
                <button class="dbg-btn" @click="cmd('continue')" :disabled="!paused" v-wjTooltip="t('Continue (F5)')">
                    <i class="pi pi-forward"></i>
                </button>
                <button class="dbg-btn" @click="cmd('stepOver')" :disabled="!paused" v-wjTooltip="t('Step over (F10)')">
                    <i class="pi pi-arrow-down-left"></i>
                </button>
                <button class="dbg-btn" @click="cmd('stepInto')" :disabled="!paused" v-wjTooltip="t('Step into (F11)')">
                    <i class="pi pi-arrow-down-right"></i>
                </button>
                <button class="dbg-btn" @click="cmd('stepOut')" :disabled="!paused" v-wjTooltip="t('Step out (Shift+F11)')">
                    <i class="pi pi-arrow-up-left"></i>
                </button>
                <button class="dbg-btn dbg-stop" @click="cmd('stop')" :disabled="!running" v-wjTooltip="t('Stop')">
                    <i class="pi pi-stop"></i>
                </button>
                <span class="dbg-status" :class="'dbg-status-' + status">{{ statusLabel }}</span>
            </div>

            <!-- main: code + console | watch + vars -->
            <div class="dbg-main">
                <div class="dbg-left">
                    <div class="dbg-code" ref="codeEl" v-visible="onVisible"></div>
                    <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                    <div class="dbg-console">
                        <div class="dbg-panel-title">
                            {{ t('Debug console') }}
                            <i class="pi pi-trash dbg-mini" v-wjTooltip="t('Clear')" @click="consoleLog = []"></i>
                        </div>
                        <div class="dbg-console-out" ref="consoleOut">
                            <div v-for="(c, i) in consoleLog" :key="i" class="dbg-console-line">
                                <div class="dbg-console-in">&rsaquo;&nbsp;{{ c.input }}</div>
                                <div v-if="c.output" class="dbg-console-res">{{ c.output }}</div>
                                <div v-if="c.error" class="dbg-console-err">{{ c.error }}</div>
                            </div>
                            <div v-if="!consoleLog.length" class="dbg-empty">
                                {{ t('Stop at a breakpoint and evaluate expressions in the current frame.') }}
                            </div>
                        </div>
                        <input class="dbg-console-input" v-model="consoleInput" @keydown.enter="runConsole"
                               :disabled="!paused" :placeholder="t('expression in the current frame… (Enter)')" />
                    </div>
                </div>

                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                <div class="dbg-right">
                    <div class="dbg-panel dbg-watch">
                        <div class="dbg-panel-title">
                            {{ t('Watch') }}
                            <i class="pi pi-plus dbg-mini" v-wjTooltip="t('Add expression')" @click="addWatch"></i>
                        </div>
                        <div class="dbg-scroll">
                            <div v-for="(w, i) in watchRows" :key="i" class="dbg-watch-row">
                                <input class="dbg-watch-expr" v-model="w.expr"
                                       @keydown.enter="applyWatch" @blur="applyWatch" :placeholder="t('expression')" />
                                <span class="dbg-watch-val" :class="{ 'dbg-err': w.error }" :title="w.error || w.value">
                                    {{ w.error || w.value }}
                                </span>
                                <i class="pi pi-times dbg-watch-del" @click="delWatch(i)"></i>
                            </div>
                            <div v-if="!watchRows.length" class="dbg-empty">{{ t('No expressions') }}</div>
                        </div>
                    </div>
                    <div class="dbg-panel dbg-vars">
                        <div class="dbg-panel-title">
                            {{ t('Variables') }}
                            <span v-if="frameLabel" class="dbg-frame">{{ frameLabel }}</span>
                        </div>
                        <div class="dbg-scroll">
                            <div v-for="(v, i) in vars" :key="i" class="dbg-var-row">
                                <span class="dbg-var-name">{{ v.name }}</span>
                                <span class="dbg-var-type">{{ v.type }}</span>
                                <span class="dbg-var-val" :title="v.value">{{ v.value }}</span>
                            </div>
                            <div v-if="!vars.length" class="dbg-empty">{{ t('— no data —') }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'
import { t } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n
import CodeMirror from 'codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/python/python.js'
import 'codemirror/theme/neo.css'

const DONE = { finished: 1, error: 1, stopped: 1 }

export default {
    props: {
        attr_id: String,
        widget_id: [String, Number],
        initialAttr: { type: Object, default: null },
        parentAttr: { type: Object, default: null },
    },
    data: () => ({
        WIDGET: {},
        attr: {},
        code: '',
        editor: null,
        breakpoints: {},        // { lineno(1-based): condition(str) }
        watchRows: [],          // [{ expr, value, error }]
        vars: [],
        stack: [],
        consoleLog: [],
        consoleInput: '',
        sid: null,
        status: 'idle',
        lastSeq: -1,
        pollTimer: null,
        pollBusy: false,
        curLine: null,
    }),
    setup() {
        const store = useMainStore()
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.store.widget_attr_set(this)
        this.code = this.store.attr_get(this.WIDGET, this.attr) || ''
    },
    mounted() {
        this.initEditor()
    },
    beforeUnmount() {
        this.stopPolling()
        if (this.editor) { try { this.editor.toTextArea?.() } catch (e) { /* noop */ } this.editor = null }
    },
    computed: {
        running() { return ['starting', 'running', 'paused'].includes(this.status) },
        paused() { return this.status === 'paused' },
        statusLabel() {
            // IE 2026 09 23 / ODOO-341407: i18n
            return {
                idle: t('not started'), starting: t('starting…'), running: t('running'),
                paused: t('paused'), finished: t('finished'), error: t('error'), stopped: t('stopped'),
            }[this.status] || this.status
        },
        frameLabel() {
            const f = this.stack && this.stack[0]
            return f ? `${f.func}:${f.line}` : ''
        },
    },
    watch: {
        // sid is delivered by the backend via updateWidget → Object.assign(WIDGET, …)
        'WIDGET.debug_session_id'(sid) {
            if (sid && sid !== this.sid) {
                this.sid = sid
                this.status = 'running'
                this.startPolling()
            }
        },
    },
    methods: {
        content_Changed() {
            // контракт attr-компонента: broadcast из WidgetAttrs.content_Changed.
            // Перечитываем код из record и обновляем редактор; активную
            // отладочную сессию не трогаем
            this.store.widget_attr_set(this)
            const code = this.store.attr_get(this.WIDGET, this.attr) || ''
            if (code !== this.code) {
                this.code = code
                if (this.editor && this.editor.getValue() !== code) {
                    this.editor.setValue(code)
                }
            }
        },
        onVisible(visible) {
            if (visible && this.editor) this.$nextTick(() => this.editor.refresh())
        },
        initEditor() {
            if (!this.$refs.codeEl) return
            this.editor = CodeMirror(this.$refs.codeEl, {
                value: this.code || '',
                mode: 'python',
                theme: 'neo',
                lineNumbers: true,
                indentUnit: 4,
                smartIndent: true,
                gutters: ['CodeMirror-linenumbers', 'dbg-bp-gutter'],
            })
            this.editor.on('gutterClick', (cm, n, gutter, e) => this.toggleBreakpoint(n, e))
            this.editor.on('change', () => { this.code = this.editor.getValue() })
            this.renderBreakpoints()
            this.$nextTick(() => this.editor.refresh())
        },

        // -------------------------------------------------- breakpoints
        toggleBreakpoint(n, e) {
            const line = n + 1
            if (e && (e.shiftKey || e.altKey)) {
                const cur = this.breakpoints[line] || ''
                const cond = window.prompt(t('Breakpoint condition (Python expression). Empty — unconditional:'), cur)   // IE 2026 09 23 / ODOO-341407: i18n
                if (cond === null) return
                this.breakpoints[line] = cond.trim()
            } else if (this.breakpoints[line] !== undefined) {
                delete this.breakpoints[line]
            } else {
                this.breakpoints[line] = ''
            }
            this.renderBreakpoints()
            this.syncBreakpoints()
        },
        renderBreakpoints() {
            if (!this.editor) return
            this.editor.clearGutter('dbg-bp-gutter')
            for (const line of Object.keys(this.breakpoints)) {
                const cond = this.breakpoints[line]
                const marker = document.createElement('div')
                marker.className = 'dbg-bp' + (cond ? ' dbg-bp-cond' : '')
                marker.innerHTML = '&#9679;'
                marker.title = cond ? t('Condition: {cond}', { cond }) : t('Breakpoint')   // IE 2026 09 23 / ODOO-341407: i18n
                this.editor.setGutterMarker(Number(line) - 1, 'dbg-bp-gutter', marker)
            }
        },
        syncBreakpoints() {
            if (this.sid && this.running) this.cmd('setBreakpoints', { breakpoints: { ...this.breakpoints } })
        },

        // -------------------------------------------------- run / step
        startDebug() {
            if (this.editor) this.code = this.editor.getValue()
            this.clearCurrentLine()
            this.consoleLog = []
            this.vars = []
            this.stack = []
            this.lastSeq = -1
            this.status = 'starting'
            this.store.executeWidgetMethod(this.WIDGET.vueObj, {
                command: 'executeWidgetMethod',
                method: 'test_debug',
                code: this.code,
                breakpoints: { ...this.breakpoints },
                watch: this.watchRows.map(w => w.expr).filter(Boolean),
            })
        },
        cmd(action, extra) {
            if (!this.sid) return
            this.store.executeBackendMethod(this.WIDGET.vueObj, {
                command: 'executeBackendMethod',
                method: 'debug_cmd',
                params: { debug_session_id: this.sid, debug_command: { action, ...(extra || {}) } },
            }, null)
            if (['continue', 'stepOver', 'stepInto', 'stepOut'].includes(action)) {
                this.status = 'running'
                this.clearCurrentLine()
            } else if (action === 'stop') {
                this.status = 'stopped'
            }
        },
        onKey(e) {
            if (!this.paused) return
            if (e.key === 'F5') { e.preventDefault(); this.cmd('continue') }
            else if (e.key === 'F10') { e.preventDefault(); this.cmd('stepOver') }
            else if (e.key === 'F11' && !e.shiftKey) { e.preventDefault(); this.cmd('stepInto') }
            else if (e.key === 'F11' && e.shiftKey) { e.preventDefault(); this.cmd('stepOut') }
        },

        // -------------------------------------------------- polling
        startPolling() {
            this.stopPolling()
            this.pollTimer = setInterval(() => this.poll(), this.store.setting('fe.polling.debuggerPollMs', 300))   // IE 2026 09 05 / ODOO-336330
            this.poll()
        },
        stopPolling() {
            if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
        },
        poll() {
            if (!this.sid || this.pollBusy) return
            this.pollBusy = true
            this.store.executeBackendMethod(this.WIDGET.vueObj, {
                command: 'executeBackendMethod',
                method: 'debug_poll',
                params: { debug_session_id: this.sid },
            }, (answer) => {
                this.pollBusy = false
                this.applyState(answer && answer.debug_state)
            })
        },
        applyState(st) {
            if (!st) return
            this.status = st.status || this.status
            if (DONE[st.status]) {
                this.stopPolling()
                this.clearCurrentLine()
                if (st.status === 'error' && st.error) {
                    this.consoleLog.push({ input: t('(evaluation error)'), output: '', error: st.error })   // IE 2026 09 23 / ODOO-341407: i18n
                }
                this.vars = []
                this.stack = []
                return
            }
            if (st.seq === this.lastSeq) return
            this.lastSeq = st.seq
            this.vars = st.vars || []
            this.stack = st.stack || []
            this.consoleLog = st.console || []
            this.mergeWatch(st.watch || [])
            if (st.status === 'paused') {
                this.setCurrentLine(st.line)
                this.$nextTick(this.scrollConsole)
            } else {
                this.clearCurrentLine()
            }
        },

        // -------------------------------------------------- console / watch
        runConsole() {
            const expr = (this.consoleInput || '').trim()
            if (!expr || !this.paused) return
            this.cmd('eval', { expr })
            this.consoleInput = ''
        },
        scrollConsole() {
            const el = this.$refs.consoleOut
            if (el) el.scrollTop = el.scrollHeight
        },
        addWatch() { this.watchRows.push({ expr: '', value: '', error: '' }) },
        delWatch(i) { this.watchRows.splice(i, 1); this.applyWatch() },
        applyWatch() {
            if (this.sid && this.running) {
                this.cmd('setWatch', { watch: this.watchRows.map(w => w.expr).filter(Boolean) })
            }
        },
        mergeWatch(watch) {
            const byExpr = {}
            for (const w of watch) byExpr[w.expr] = w
            for (const row of this.watchRows) {
                const w = byExpr[(row.expr || '').trim()]
                row.value = w ? w.value : ''
                row.error = w ? w.error : ''
            }
        },

        // -------------------------------------------------- current line
        setCurrentLine(line) {
            this.clearCurrentLine()
            if (!this.editor || !line) return
            const l = line - 1
            this.curLine = l
            this.editor.addLineClass(l, 'background', 'dbg-current-line')
            this.editor.addLineClass(l, 'gutter', 'dbg-current-gutter')
            this.editor.scrollIntoView({ line: l, ch: 0 }, 120)
        },
        clearCurrentLine() {
            if (this.editor && this.curLine != null) {
                this.editor.removeLineClass(this.curLine, 'background', 'dbg-current-line')
                this.editor.removeLineClass(this.curLine, 'gutter', 'dbg-current-gutter')
            }
            this.curLine = null
        },
    },
}
</script>

<style>
.dbg-root { display: flex; flex-direction: column; height: 100%; min-height: 420px; font-size: 12px; outline: none; }
.dbg-toolbar { display: flex; align-items: center; gap: 4px; padding: 4px 6px; border-bottom: 1px solid #e0e0e0; flex: 0 0 auto; }
.dbg-btn { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #cfd6df; background: #fff; border-radius: 4px; padding: 3px 8px; cursor: pointer; color: #37474f; }
.dbg-btn:hover:not(:disabled) { background: #eef3f8; }
.dbg-btn:disabled { opacity: .4; cursor: default; }
.dbg-test { color: #2e7d32; border-color: #a5d6a7; font-weight: 600; }
.dbg-stop { color: #c62828; }
.dbg-sep { width: 1px; height: 20px; background: #e0e0e0; margin: 0 4px; }
.dbg-status { margin-left: auto; font-size: 11px; padding: 2px 8px; border-radius: 10px; background: #eceff1; color: #546e7a; }
.dbg-status-paused { background: #fff3e0; color: #e65100; }
.dbg-status-running, .dbg-status-starting { background: #e3f2fd; color: #1565c0; }
.dbg-status-error { background: #ffebee; color: #c62828; }
.dbg-status-finished { background: #e8f5e9; color: #2e7d32; }

.dbg-main { flex: 1 1 auto; display: flex; min-height: 0; }
.dbg-left { flex: 1 1 auto; display: flex; flex-direction: column; min-width: 0; border-right: 1px solid #e0e0e0; }
.dbg-code { flex: 1 1 60%; min-height: 160px; overflow: hidden; }
.dbg-code .CodeMirror { height: 100%; }
.dbg-console { flex: 1 1 40%; display: flex; flex-direction: column; border-top: 1px solid #e0e0e0; min-height: 120px; }
.dbg-console-out { flex: 1 1 auto; overflow: auto; padding: 4px 8px; font-family: monospace; background: #fafafa; }
.dbg-console-line { margin-bottom: 2px; }
.dbg-console-in { color: #1565c0; white-space: pre-wrap; }
.dbg-console-res { color: #263238; white-space: pre-wrap; }
.dbg-console-err { color: #c62828; white-space: pre-wrap; }
.dbg-console-input { border: none; border-top: 1px solid #e0e0e0; padding: 5px 8px; font-family: monospace; outline: none; }
.dbg-console-input:disabled { background: #f5f5f5; }

.dbg-right { flex: 0 0 320px; display: flex; flex-direction: column; min-width: 0; }
.dbg-panel { display: flex; flex-direction: column; min-height: 0; }
.dbg-watch { flex: 1 1 40%; border-bottom: 1px solid #e0e0e0; }
.dbg-vars { flex: 1 1 60%; }
.dbg-panel-title { flex: 0 0 auto; display: flex; align-items: center; gap: 6px; padding: 4px 8px; font-weight: 600; color: #455a64; background: #f4f6f8; border-bottom: 1px solid #eceff1; }
.dbg-frame { margin-left: auto; font-weight: 400; font-family: monospace; color: #90a4ae; }
.dbg-mini { margin-left: auto; cursor: pointer; color: #90a4ae; }
.dbg-mini:hover { color: #546e7a; }
.dbg-scroll { flex: 1 1 auto; overflow: auto; }

.dbg-watch-row { display: flex; align-items: center; gap: 6px; padding: 2px 8px; border-bottom: 1px dotted #eceff1; }
.dbg-watch-expr { flex: 0 0 40%; border: 1px solid #eceff1; border-radius: 3px; padding: 1px 4px; font-family: monospace; }
.dbg-watch-val { flex: 1 1 auto; font-family: monospace; color: #263238; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dbg-watch-val.dbg-err { color: #c62828; }
.dbg-watch-del { cursor: pointer; color: #cfd6df; }
.dbg-watch-del:hover { color: #c62828; }

.dbg-var-row { display: flex; gap: 6px; padding: 2px 8px; border-bottom: 1px dotted #eceff1; }
.dbg-var-name { flex: 0 0 34%; color: #6a1b9a; font-family: monospace; overflow: hidden; text-overflow: ellipsis; }
.dbg-var-type { flex: 0 0 22%; color: #90a4ae; font-family: monospace; overflow: hidden; text-overflow: ellipsis; }
.dbg-var-val { flex: 1 1 auto; color: #263238; font-family: monospace; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dbg-empty { padding: 8px; color: #b0bec5; font-style: italic; }

/* breakpoint gutter */
.CodeMirror .dbg-bp-gutter { width: 14px; }
.dbg-bp { color: #e53935; font-size: 12px; line-height: 1; padding-left: 1px; cursor: pointer; }
.dbg-bp-cond { color: #fb8c00; }
/* current execution line */
.CodeMirror .dbg-current-line { background: #fff8e1 !important; }
.CodeMirror .dbg-current-gutter { background: #ffe082 !important; }
</style>
